import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Ticket, Users, Coins, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OperatorDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            const response = await axios.get(`${API}/api/operator/dashboard`, { withCredentials: true });
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const redeemData = [
        { day: 'Mon', count: 12 },
        { day: 'Tue', count: 19 },
        { day: 'Wed', count: 8 },
        { day: 'Thu', count: 15 },
        { day: 'Fri', count: 22 },
        { day: 'Sat', count: 28 },
        { day: 'Sun', count: 25 },
    ];

    if (loading) {
        return (
            <DashboardLayout type="operator">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    const stats = [
        { title: 'Total Codes', value: data?.total_codes || 0, icon: Ticket, color: 'text-blue-400' },
        { title: 'Active Codes', value: data?.active_codes || 0, icon: Ticket, color: 'text-emerald-400' },
        { title: 'Redemptions', value: data?.total_redemptions || 0, icon: Users, color: 'text-purple-400' },
        { title: 'Credits', value: data?.operator?.credits || 0, icon: Coins, color: 'text-gold' },
    ];

    return (
        <DashboardLayout type="operator">
            <div className="space-y-8" data-testid="operator-dashboard">
                {/* Header */}
                <div>
                    <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">
                        Welcome, {data?.operator?.company_name || 'Operator'}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Plan: <span className="text-gold capitalize">{data?.operator?.membership_plan || 'Starter'}</span>
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {stats.map((stat, i) => (
                        <Card key={stat.title} className="bg-[#121620] border-white/10 animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                                </div>
                                <p className="text-2xl font-bold text-white">{stat.value}</p>
                                <p className="text-xs text-muted-foreground mt-1">{stat.title}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Redemption Chart */}
                <Card className="bg-[#121620] border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white font-outfit">Redemptions This Week</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={redeemData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1A202C" />
                                    <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} />
                                    <YAxis stroke="#94A3B8" fontSize={12} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#121620', 
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px'
                                        }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="grid md:grid-cols-3 gap-4">
                    <Card className="bg-[#121620] border-white/10 p-6 cursor-pointer hover:border-gold/30 transition-colors" onClick={() => window.location.href = '/operator/codes'}>
                        <Ticket className="w-8 h-8 text-gold mb-4" />
                        <h3 className="font-outfit font-semibold text-white mb-2">Generate Codes</h3>
                        <p className="text-sm text-muted-foreground">Create redeem codes for your customers</p>
                    </Card>
                    <Card className="bg-[#121620] border-white/10 p-6 cursor-pointer hover:border-gold/30 transition-colors" onClick={() => window.location.href = '/operator/branding'}>
                        <div className="w-8 h-8 rounded-md bg-gradient-to-br from-gold to-gold/50 mb-4" />
                        <h3 className="font-outfit font-semibold text-white mb-2">Customize Branding</h3>
                        <p className="text-sm text-muted-foreground">Set up your white-label experience</p>
                    </Card>
                    <Card className="bg-[#121620] border-white/10 p-6 cursor-pointer hover:border-gold/30 transition-colors" onClick={() => window.location.href = '/operator/credits'}>
                        <Coins className="w-8 h-8 text-gold mb-4" />
                        <h3 className="font-outfit font-semibold text-white mb-2">Buy Credits</h3>
                        <p className="text-sm text-muted-foreground">Purchase credits for more redemptions</p>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
}
